import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import JVChartPanel, { CustomTooltip } from './JVChartPanel';
import { paddedExtent } from '../utils/chartDomain';

const fmt = (v, d = 3) => v != null ? Number(v).toFixed(d) : '—';

// Multiply each RGB channel by (1 - amt) to darken a hex colour for
// light-mode contrast on the validation banners.
const darken = (hex, amt = 0.45) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const v = parseInt(m[1], 16);
  const f = x => Math.round(x * (1 - amt)).toString(16).padStart(2, '0');
  return `#${f((v >> 16) & 0xff)}${f((v >> 8) & 0xff)}${f(v & 0xff)}`;
};

const BandTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0d1117', border: '1px solid #2a3f55', borderRadius: 4,
      padding: '10px 14px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
    }}>
      <div style={{ color: '#8ba3bb', marginBottom: 6 }}>x = {Number(label).toFixed(0)} nm</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: {Number(p.value).toFixed(4)} eV
        </div>
      ))}
    </div>
  );
};

export default function ResultsPanel({ results, loading, error, theme = 'dark' }) {
  // Hooks must be called unconditionally before any early returns
  const c = themeColors(theme);
  const colors = c;
  const axisStyle = { fill: c.plotText, fontSize: 14, fontFamily: 'IBM Plex Mono' };
  const tickStyle = { fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' };

  // Validation banners on by default; persists across sessions.
  const [validationOn, setValidationOn] = useState(() => {
    try { return localStorage.getItem('jvista_validation_on') !== '0'; }
    catch { return true; }
  });
  const toggleValidation = () => {
    setValidationOn(v => {
      const next = !v;
      try { localStorage.setItem('jvista_validation_on', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const mRaw = React.useMemo(() => results?.metrics || {}, [results]);
  const jvLight = React.useMemo(() => results?.jv_curve || [], [results]);

  // Frontend CurVA extraction when backend doesn't provide J0/A/Rs
  const m = React.useMemo(() => {
    const out = { ...mRaw };
    if (out.J0 == null && jvLight.length > 5) {
      const KTQ = 0.02585;
      const V = jvLight.map(p => p.V), J = jvLight.map(p => p.J);
      const i0 = V.reduce((bi, v, i) => Math.abs(v) < Math.abs(V[bi]) ? i : bi, 0);
      const Jsc = Math.abs(J[i0]);
      // Estimate Voc
      let Voc = 0.9;
      for (let i = 0; i < V.length - 1; i++) {
        if ((J[i] < 0 && J[i+1] >= 0) || (J[i] >= 0 && J[i+1] < 0)) {
          const f = -J[i] / (J[i+1] - J[i]);
          Voc = V[i] + f * (V[i+1] - V[i]); break;
        }
      }
      // log(J+Jsc) vs V for J0/A extraction
      const logPts = [];
      const vLo = Voc * 0.15, vHi = Voc * 0.80;
      for (let i = 0; i < V.length; i++) {
        const Jw = J[i] + Jsc;
        if (V[i] >= vLo && V[i] <= vHi && Jw > 1e-8) {
          const lj = Math.log10(Jw);
          if (isFinite(lj)) logPts.push({ v: V[i], lj });
        }
      }
      if (logPts.length >= 2) {
        const n = logPts.length;
        const mx = logPts.reduce((s, p) => s + p.v, 0) / n;
        const my = logPts.reduce((s, p) => s + p.lj, 0) / n;
        let num = 0, den = 0;
        for (const p of logPts) { num += (p.v - mx) * (p.lj - my); den += (p.v - mx) ** 2; }
        if (Math.abs(den) > 1e-12) {
          const slope = num / den, intercept = my - slope * mx;
          if (slope > 0) {
            const A = 1 / (slope * KTQ * 2.302585);
            const J0 = Math.pow(10, intercept);
            if (A > 0.1 && A < 10) out.A_logJV = A;
            if (J0 > 1e-30 && J0 < 1e3) out.J0 = J0;
          }
        }
      }
      // dV/dJ vs 1/(J+Jsc) for Rs extraction (Sites method)
      const dvdjPts = [];
      for (let i = 1; i < V.length; i++) {
        const dv = V[i] - V[i-1], dj = J[i] - J[i-1];
        const Jm = 0.5 * (J[i] + J[i-1]);
        const Jdark = Jm + Jsc;
        if (Math.abs(dj) > 1e-6 && Jdark > 0.5) {
          const dvdj = (dv / dj) * 1000;
          const invJ = 1 / Jdark;
          if (isFinite(dvdj) && dvdj > 0 && dvdj < 50 && invJ > 0 && invJ < 2)
            dvdjPts.push({ invJ, dvdj });
        }
      }
      if (dvdjPts.length >= 2) {
        const n = dvdjPts.length;
        const mx = dvdjPts.reduce((s, p) => s + p.invJ, 0) / n;
        const my = dvdjPts.reduce((s, p) => s + p.dvdj, 0) / n;
        let num = 0, den = 0;
        for (const p of dvdjPts) { num += (p.invJ - mx) * (p.dvdj - my); den += (p.invJ - mx) ** 2; }
        if (Math.abs(den) > 1e-12) {
          const slope = num / den, intercept = my - slope * mx;
          if (intercept >= 0 && isFinite(intercept)) out.Rs = intercept;
          const A = slope * 1e-3 / KTQ;
          if (A > 0.1 && A < 10) out.A_dVdJ = A;
        }
      }
      // Rsh from dJ/dV at V=0
      if (jvLight.length > 2) {
        const i0v = V.reduce((bi, v, i) => Math.abs(v) < Math.abs(V[bi]) ? i : bi, 0);
        const lo = Math.max(0, i0v - 1), hi = Math.min(V.length - 1, i0v + 1);
        if (hi > lo) {
          const djdv = (J[hi] - J[lo]) / (V[hi] - V[lo]);
          if (Math.abs(djdv) > 1e-10) out.Rsh = 1 / Math.abs(djdv) * 1000;
        }
      }
    }
    return out;
  }, [mRaw, jvLight]);

  if (loading) {
    return (
      <div className="results-panel">
        <div className="loading-state">
          <div className="loading-bar-wrap"><div className="loading-bar" /></div>
          <div className="loading-text">Solving drift-diffusion equations</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-panel">
        <div className="empty-state">
          <div className="empty-icon">✕</div>
          <div className="empty-title">Simulation Error</div>
          <div className="empty-sub" style={{ color: '#ff4757' }}>
            {typeof error === 'string' ? error : (error?.message || error?.detail || JSON.stringify(error))}
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="results-panel">
        <div className="empty-state">
          <div className="empty-icon">◈</div>
          <div className="empty-title">No Results</div>
          <div className="empty-sub">
            Configure device parameters and run simulation to see JV curves, band diagrams, and extracted metrics.
          </div>
        </div>
      </div>
    );
  }

  const jvDark  = results.jv_dark  || [];
  const bd = results.band_diagram || [];
  const warnings = results.warnings || [];
  const flags = results.flags || [];
  const isLight = theme === 'light';

  // Non-critical validation flags render as a small mark beside the
  // metric they refer to; the full message appears on hover. Keeps the
  // results readable while the context stays one hover away.
  const FlagMark = ({ param }) => {
    if (!validationOn) return null;
    const hits = flags.filter(
      f => f.parameter === param && f.severity !== 'critical');
    if (!hits.length) return null;
    const worst = hits.some(f => f.severity === 'warning') ? 'warning' : 'info';
    const color = worst === 'warning' ? '#ff8800' : '#d4a900';
    return (
      <span
        title={hits.map(f =>
          `${f.severity === 'warning' ? '⚠ ' : 'ℹ '}${f.message}`).join('\n')}
        style={{ marginLeft: 6, color, cursor: 'help',
                 fontSize: 13, verticalAlign: 'middle' }}
      >
        {worst === 'warning' ? '⚠' : 'ℹ'}
      </span>
    );
  };

  const bdData = bd.map(pt => ({
    x: pt.x_nm,
    Ec: pt.Ec, Ev: pt.Ev, EFn: pt.Efn, EFp: pt.Efp,
  }));

  // Dark log-J: dark forward injection current comes back as J < 0 from backend
  // (opposite sign to photocurrent). Use |J| for the log plot.
  const logJVData = jvDark
    .filter(pt => pt.V > 0.05 && Math.abs(pt.J) > 1e-10 && isFinite(pt.J) && Math.abs(pt.J) < 500)
    .map(pt => ({ V: pt.V, 'log(J)': Math.log10(Math.abs(pt.J)) }));

  // Data-driven axis domains (~5% padding) — no hardcoded windows.
  const bdXDomain = paddedExtent(bdData.map(p => p.x), 0.02) || [0, 'auto'];
  const bdYDomain = paddedExtent(
    bdData.flatMap(p => [p.Ec, p.Ev, p.EFn, p.EFp]), 0.05) || ['auto', 'auto'];
  const logXDomain = paddedExtent(logJVData.map(p => p.V), 0.05) || ['auto', 'auto'];
  const logYDomain = paddedExtent(logJVData.map(p => p['log(J)']), 0.05) || ['auto', 'auto'];

  // Metrics display — Jsc/Jmp shown as positive magnitudes (industry standard readout)
  const Jsc_display = m.Jsc != null ? Math.abs(m.Jsc).toFixed(2) : '—';
  const Jmp_display = m.Jmp != null ? Math.abs(m.Jmp).toFixed(3) : '—';

  return (
    <div className="results-panel" id="simulation-results">

      {/* Header strip — validation toggle */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end',
        padding: '4px 0 6px',
      }}>
        <button
          onClick={toggleValidation}
          title="Flag results that violate the Shockley-Queisser limit or beat published records"
          style={{
            background: validationOn ? c.accent + '22' : c.btnSecondary,
            color: validationOn ? c.accent : c.textMuted,
            border: `1px solid ${validationOn ? c.accent : c.border}`,
            borderRadius: 4, padding: '4px 10px', cursor: 'pointer',
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
          }}
        >
          🔍 Validation: {validationOn ? 'ON' : 'OFF'}
        </button>
      </div>

      {warnings.filter(w => w).length > 0 && (
        <div className="warnings-bar">
          <span className="warn-icon">⚠</span>
          <div className="warn-text">{warnings.filter(w => w).join(' · ')}</div>
        </div>
      )}

      {/* JV solve method badge (Phase 1B) — only rendered when the
          backend reports jv_method ('coupled' | 'superposition'); older
          saved sims lack it and show nothing. Amber note when coupled
          points were filled by the superposition fallback. jv_auto_reason
          (V1.0 honesty guard) explains why jv_mode='auto' escalated to
          the coupled solve (or tried to). */}
      {(results.jv_method || results.jv_auto_reason) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          margin: '2px 0 6px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
        }}>
          {results.jv_method && (
            <span
              title={results.jv_method === 'coupled'
                ? 'Illuminated JV solved fully coupled at every bias — the photocurrent J_ph(V) is bias dependent (voltage-dependent collection)'
                : 'Illuminated JV built as Jsc + J_dark(V) (superposition approximation) — photocurrent constant by construction, no voltage-dependent collection'}
              style={{
                padding: '2px 8px', borderRadius: 4,
                border: `1px solid ${results.jv_method === 'coupled' ? c.accent : c.border}`,
                color: results.jv_method === 'coupled' ? c.accent : c.textMuted,
                background: results.jv_method === 'coupled' ? c.accent + '18' : 'transparent',
              }}
            >
              {results.jv_method === 'coupled' ? 'Coupled JV' : 'Superposition JV'}
            </span>
          )}
          {results.jv_auto_reason && (
            <span
              title={results.jv_auto_reason}
              style={{
                padding: '2px 8px', borderRadius: 4,
                border: `1px solid ${c.amber || '#fb8500'}`,
                color: c.amber || '#fb8500',
                background: (c.amber || '#fb8500') + '18',
              }}
            >
              Auto: {results.jv_auto_reason}
            </span>
          )}
          {results.jv_fallback_count > 0 && (() => {
            // Fills strictly beyond Voc (far-forward diode regime) leave
            // the power quadrant and every metric fully coupled — show a
            // muted note instead of an alarm.
            const voc = Number(results?.metrics?.Voc);
            const pm = results.jv_point_method || [];
            const Vs = (results.jv_curve || []).map(p => p.V);
            const beyond = Number.isFinite(voc) && voc > 0 && pm.length === Vs.length
              && pm.every((m, i) => m !== 'superposition' || Vs[i] > voc + 0.02);
            return (
              <span
                style={{ color: beyond ? c.textDim : (c.amber || '#fb8500') }}
                title={beyond
                  ? 'Only far-forward points past Voc were filled from superposition; Jsc/Voc/FF/PCE and the collection analysis are fully coupled.'
                  : 'Some points inside the sweep could not be solved fully coupled and were filled from superposition — treat metrics near/above the stall bias with care.'}
              >
                {beyond ? 'ⓘ' : '⚠'} {results.jv_fallback_count} point{results.jv_fallback_count !== 1 ? 's' : ''}
                {beyond ? ' beyond Voc' : ''} filled by superposition fallback
                {results.coupled_stall_V != null
                  ? ` (stalled at ${Number(results.coupled_stall_V).toFixed(2)} V)` : ''}
              </span>
            );
          })()}
        </div>
      )}

      {/* v1.1.0 — trap levels are energy-resolved SRH centres in the solver;
          surface the per-layer effective minority lifetime the backend
          reports (trap_levels[].tau_eff_*_ns) so "defects ↔ lifetime" is
          visibly connected. */}
      {Array.isArray(results.trap_levels) && results.trap_levels.length > 0 && (() => {
        const byLayer = new Map();
        for (const t of results.trap_levels) {
          if (!byLayer.has(t.layer)) byLayer.set(t.layer, { n: 0, t });
          byLayer.get(t.layer).n += 1;
        }
        const fmtNs = (x) => (x == null || !isFinite(x)) ? '—'
          : x >= 1000 ? `${(x / 1000).toPrecision(3)} µs` : x >= 1 ? `${x.toPrecision(3)} ns` : `${(x * 1000).toPrecision(3)} ps`;
        return (
          <div style={{ fontSize: 11, color: c.textMuted, margin: '0 0 8px', lineHeight: 1.5,
                        fontFamily: 'IBM Plex Mono, monospace' }}
               title="Each trap level is a separate energy-resolved SRH centre (own n1/p1); τ shown is the low-injection minority lifetime with background τ + all levels of that layer">
            {[...byLayer.values()].map(({ n, t }, i) => {
              const minority = t.majority === 'p' ? 'n' : 'p';
              const eff = t.majority === 'p' ? t.tau_eff_n_ns : t.tau_eff_p_ns;
              const bg = t.majority === 'p' ? t.tau_bg_n_ns : t.tau_bg_p_ns;
              return (
                <span key={t.layer} style={{ marginRight: 14 }}>
                  {i === 0 ? 'Defects → lifetime: ' : ''}
                  <b>{t.layer}</b> {n} level{n !== 1 ? 's' : ''} → τ<sub>{minority}</sub>,eff ≈ {fmtNs(eff)}
                  {bg != null ? ` (background ${fmtNs(bg)})` : ''}
                </span>
              );
            })}
          </div>
        );
      })()}

      {/* Primary metrics — Eg (effective) tile leads when the backend
          reports metrics.absorber_Eg_eV (grading-aware minimum absorber
          bandgap); hidden for older saved sims that lack it. The bar's
          CSS grid is a fixed 4 columns, so widen to 5 inline. */}
      <div
        className="metrics-bar"
        style={m.absorber_Eg_eV != null
          ? { gridTemplateColumns: 'repeat(5, 1fr)' } : undefined}
      >
        {m.absorber_Eg_eV != null && (
          <div className="metric-cell">
            <div className="metric-name">E<sub>g</sub> (effective)</div>
            <div className="metric-value">{fmt(m.absorber_Eg_eV, 3)}<span className="metric-unit">eV</span></div>
          </div>
        )}
        <div className="metric-cell">
          <div className="metric-name">
            V<sub>oc</sub>
            {/* PART 3 — Avarodha ceiling warning. Shows when current Voc
                is within 50 mV of the computed (delta-mu-star over q)
                ceiling. Indicator only renders when
                avarodha_outputs.voc_ceiling is present AND the gap is
                small enough. Hover for the explanatory tooltip. */}
            {(() => {
              const ao = results.avarodha_outputs;
              if (!ao || ao.voc_ceiling == null || m.Voc == null) return null;
              const gap = ao.voc_ceiling - m.Voc;
              if (gap > 0.05 || gap < 0) return null;
              return (
                <span
                  title={`⚠ Approaching Avarodha ceiling — further Voc gains require microstructural improvement (current ${fmt(m.Voc, 3)} V vs ceiling ${fmt(ao.voc_ceiling, 3)} V, gap ${fmt(gap * 1000, 0)} mV)`}
                  style={{
                    marginLeft: 6, color: '#fb8500', cursor: 'help',
                    fontSize: 14, verticalAlign: 'middle',
                  }}
                >
                  ⚠
                </span>
              );
            })()}
          </div>
          <div className="metric-value highlight">{fmt(m.Voc, 4)}<span className="metric-unit">V</span><FlagMark param="Voc" /></div>
        </div>
        <div className="metric-cell">
          <div className="metric-name">J<sub>sc</sub><FlagMark param="Jsc" /></div>
          <div className="metric-value">{Jsc_display}<span className="metric-unit">mA/cm²</span></div>
        </div>
        <div className="metric-cell">
          <div className="metric-name">FF<FlagMark param="FF" /></div>
          <div className="metric-value">{fmt(m.FF, 1)}<span className="metric-unit">%</span></div>
        </div>
        <div className="metric-cell">
          <div className="metric-name">PCE<FlagMark param="PCE" /></div>
          <div className="metric-value highlight">{fmt(m.PCE, 2)}<span className="metric-unit">%</span></div>
        </div>
      </div>

      {/* Validation flags: ONLY critical stays as a banner (results
          unreliable — must not be hover-only). Warning/info tiers render
          as small marks on the relevant metric tile (see FlagMark). */}
      {validationOn && flags.some(f => f.severity === 'critical') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
          {flags.filter(f => f.severity === 'critical').map((f, i) => (
            <div key={i} style={{
              background: f.color + '22',
              border: `1px solid ${f.color}`,
              borderRadius: 6,
              padding: '6px 12px',
              color: isLight ? darken(f.color) : f.color,
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 13,
            }}>
              ⚠️ CRITICAL: {f.message}
            </div>
          ))}
        </div>
      )}

      {/* Secondary metrics */}
      <div className="secondary-metrics">
        {[
          ['Vmp',       fmt(m.Vmp, 3),                              'V'],
          ['Jmp',       Jmp_display,                                'mA/cm²'],
          ['Rs',        m.Rs   != null ? fmt(m.Rs,  3) : '—',      'Ω·cm²'],
          ['Rsh',       m.Rsh  != null ? fmt(m.Rsh, 0) : '—',     'Ω·cm²'],
          ['J₀',        m.J0   != null ? m.J0.toExponential(2) : '—', 'mA/cm²'],
          ['A (log J)', m.A_logJV != null ? fmt(m.A_logJV, 3) : '—', ''],
          ['A (dV/dJ)', m.A_dVdJ  != null ? fmt(m.A_dVdJ,  3) : '—', ''],
          ['Pmp',       fmt(m.Pmp, 2),                              'mW/cm²'],
        ].map(([name, val, unit]) => (
          <div key={name} className="sec-metric">
            <div className="sec-name">{name}</div>
            <div className="sec-value">{val} <span style={{ color: '#4a6278', fontSize: 11 }}>{unit}</span></div>
          </div>
        ))}
      </div>

      {/* Avarodha recombination analysis + J0 what-if editor moved to the
          dedicated Avarodha tab (AvarodhaPanel.js) — 2026-07 UI batch 3. */}

      {/* Charts */}
      <div className="charts-area">

        {/* JV Curve — shared with the Avarodha tab (JVChartPanel) */}
        <JVChartPanel
          jvLight={jvLight}
          jvDark={jvDark}
          jvPhoto={results.jv_photocurrent || null}
          collection={results.collection_summary || null}
          metrics={mRaw}
          theme={theme}
          chartId="jv-chart-container"
        />

        {/* Band Diagram */}
        <div className="chart-panel">
          <div className="chart-title">Band Diagram — Short Circuit</div>
          {bdData.length > 0 ? (
            <div id="band-diagram-container" style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={bdData} margin={{ top: 8, right: 20, bottom: 28, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                <XAxis dataKey="x" type="number" domain={bdXDomain}
                  tickFormatter={v => v.toFixed(0)} tick={tickStyle}
                  label={{ value: 'Position (nm)', position: 'insideBottom', offset: -14, ...axisStyle }} />
                <YAxis domain={bdYDomain} tickFormatter={v => v.toFixed(2)} tick={tickStyle}
                  label={{ value: 'Energy (eV)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                <Tooltip content={<BandTooltip />} />
                <Line type="linear" dataKey="Ec"  stroke={colors.accent} strokeWidth={2}   dot={false} name="Ec" />
                <Line type="linear" dataKey="Ev"  stroke={colors === undefined || colors.plotBg === '#ffffff' ? '#2b6cb0' : '#4a9eff'} strokeWidth={2}   dot={false} name="Ev" />
                <Line type="linear" dataKey="EFn" stroke={colors.green}  strokeWidth={1.5} dot={false} strokeDasharray="5 3" name="EFn" />
                <Line type="linear" dataKey="EFp" stroke={colors.amber}  strokeWidth={1.5} dot={false} strokeDasharray="5 3" name="EFp" />
              </LineChart>
            </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#4a6278', fontFamily: 'IBM Plex Mono', fontSize: 13 }}>
              Band diagram — coming soon
            </div>
          )}
          <PlotExportBar
            chartId="band-diagram-container"
            csvData={bd.map(pt => ({
              position_nm: pt.x_nm,
              Ec_eV: pt.Ec, Ev_eV: pt.Ev,
              EFn_eV: pt.Efn, EFp_eV: pt.Efp,
            }))}
            csvFilename="jvista_band_diagram"
            plotFilename="band_diagram"
            c={c}
            disabled={bd.length === 0}
          />
        </div>

        {/* log JV dark */}
        <div className="chart-panel">
          <div className="chart-title">log(J) — Dark Characteristic</div>
          {logJVData.length > 0 ? (
            <div id="log-jv-container" style={{ width: '100%', height: 230 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={logJVData} margin={{ top: 8, right: 20, bottom: 28, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                <XAxis dataKey="V" type="number" domain={logXDomain}
                  tickFormatter={v => v.toFixed(2)} tick={tickStyle}
                  label={{ value: 'Voltage (V)', position: 'insideBottom', offset: -14, ...axisStyle }} />
                <YAxis domain={logYDomain} tickFormatter={v => v.toFixed(1)} tick={tickStyle}
                  label={{ value: 'log₁₀(J)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="log(J)" stroke={colors.green} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#4a6278', fontFamily: 'IBM Plex Mono', fontSize: 13 }}>
              Dark JV unavailable
            </div>
          )}
          <PlotExportBar
            chartId="log-jv-container"
            csvData={logJVData.map(pt => ({ V: pt.V, logJ_dark: pt['log(J)'] }))}
            csvFilename="jvista_log_jv"
            plotFilename="log_jv"
            c={c}
            disabled={logJVData.length === 0}
          />
        </div>

        {/* CurVA Summary */}
        <div className="chart-panel">
          <div className="chart-title">CurVA Analysis Summary</div>
          <div id="curva-summary-container" style={{ fontFamily: 'IBM Plex Mono', fontSize: 13, lineHeight: 2.2 }}>
            {[
              ['Voc',        fmt(m.Voc, 4),                                  'V'],
              ['Jsc',        Jsc_display,                                     'mA/cm²'],
              ['FF',         fmt(m.FF,  2),                                   '%'],
              ['PCE',        fmt(m.PCE, 3),                                   '%'],
              ['Vmp',        fmt(m.Vmp, 3),                                   'V'],
              ['Jmp',        Jmp_display,                                     'mA/cm²'],
              ['J₀',         m.J0     != null ? m.J0.toExponential(3) : '—', 'mA/cm²'],
              ['Rs',         m.Rs     != null ? fmt(m.Rs,  4) : '—',         'Ω·cm²'],
              ['Rsh',        m.Rsh    != null ? fmt(m.Rsh, 1) : '—',        'Ω·cm²'],
              ['A (log JV)', m.A_logJV != null ? fmt(m.A_logJV, 4) : '—',   ''],
              ['A (dV/dJ)',  m.A_dVdJ  != null ? fmt(m.A_dVdJ,  4) : '—',  ''],
            ].map(([name, val, unit]) => (
              <div key={name} style={{ display: 'flex', justifyContent: 'space-between',
                borderBottom: '1px solid #1e2d3d', paddingBottom: 1 }}>
                <span style={{ color: '#8ba3bb' }}>{name}</span>
                <span style={{ color: '#e8edf2' }}>{val} <span style={{ color: '#4a6278', fontSize: 11 }}>{unit}</span></span>
              </div>
            ))}
          </div>
          <PlotExportBar
            chartId="curva-summary-container"
            csvData={[
              { parameter: 'Voc',        value: m.Voc,     units: 'V' },
              { parameter: 'Jsc',        value: m.Jsc != null ? Math.abs(m.Jsc) : null, units: 'mA/cm2' },
              { parameter: 'FF',         value: m.FF,      units: '%' },
              { parameter: 'PCE',        value: m.PCE,     units: '%' },
              { parameter: 'Vmp',        value: m.Vmp,     units: 'V' },
              { parameter: 'Jmp',        value: m.Jmp != null ? Math.abs(m.Jmp) : null, units: 'mA/cm2' },
              { parameter: 'J0',         value: m.J0,      units: 'mA/cm2' },
              { parameter: 'Rs',         value: m.Rs,      units: 'ohm.cm2' },
              { parameter: 'Rsh',        value: m.Rsh,     units: 'ohm.cm2' },
              { parameter: 'A_logJV',    value: m.A_logJV, units: '' },
              { parameter: 'A_dVdJ',     value: m.A_dVdJ,  units: '' },
            ]}
            csvFilename="jvista_curva_summary"
            plotFilename="curva_summary"
            c={c}
            disabled={!m.Voc}
          />
        </div>

      </div>
    </div>
  );
}
